import { createFileRoute } from '@tanstack/react-router';
import { Shield, Lock, FileCheck, AlertTriangle, Check } from "lucide-react";
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/use-cases/enterprise-security')({
  component: EnterpriseSecurityUseCasePage,
  head: () => ({
    meta: [
      { title: 'Enterprise Security | Matricks' },
      { name: 'description', content: 'Implement zero-trust security across all your APIs and integrations. Fine-grained access control, audit logging, and compliance features.' },
    ],
  }),
});

const challenges = [
  "Sensitive data exposed through APIs without proper controls",
  "Inconsistent security policies across integrations",
  "Limited visibility into who accessed what and when",
  "Compliance requirements (SOC2, GDPR, HIPAA) difficult to meet",
  "Credential sprawl and poor secrets management",
];

const solutions = [
  {
    iconName: "Shield",
    title: "Zero-Trust Architecture",
    description: "Every request authenticated and authorized. No implicit trust anywhere.",
  },
  {
    iconName: "Lock",
    title: "Fine-Grained Policies",
    description: "Control access at API, resource, and field level with powerful policy language.",
  },
  {
    iconName: "Eye",
    title: "Audit Logging",
    description: "Comprehensive, immutable logs of all access for compliance and forensics.",
  },
  {
    iconName: "FileCheck",
    title: "Compliance Ready",
    description: "Built-in support for SOC2, GDPR, HIPAA, and other compliance requirements.",
  },
];

const securityCapabilities = [
  {
    category: "Access Control",
    items: ["Role-based access control (RBAC)", "Attribute-based access control (ABAC)", "Field-level permissions", "Time-based restrictions"],
  },
  {
    category: "Authentication",
    items: ["SSO/SAML integration", "Multi-factor authentication", "API key management", "OAuth 2.0 support"],
  },
  {
    category: "Data Protection",
    items: ["Encryption at rest (AES-256)", "Encryption in transit (TLS 1.3)", "Secrets management", "Data masking"],
  },
  {
    category: "Monitoring",
    items: ["Real-time threat detection", "Anomaly alerting", "Access pattern analysis", "Security dashboards"],
  },
];

const faqItems = [
  {
    question: "What compliance certifications do you have?",
    answer: "Matricks is SOC2 Type II certified and supports GDPR, HIPAA, and other compliance requirements. We provide Business Associate Agreements for healthcare customers and Data Processing Agreements for EU customers.",
  },
  {
    question: "How do you handle secrets and credentials?",
    answer: "All secrets are encrypted using AES-256 before storage. We integrate with enterprise secrets managers like HashiCorp Vault. Access to secrets is controlled by policies and all access is logged.",
  },
  {
    question: "Can you support our custom security requirements?",
    answer: "Yes, enterprise plans include custom security configurations, dedicated security reviews, and integration with your existing security infrastructure. Contact our security team to discuss your requirements.",
  },
];

function EnterpriseSecurityUseCasePage() {
  return (
    <div>
      <ContentHeader
        title="Enterprise Security"
        description="Secure all your APIs and integrations with zero-trust architecture. Meet compliance requirements while maintaining developer productivity."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Use Cases", href: "/use-cases" },
          { label: "Enterprise Security" },
        ]}
        badge="Use Case"
      />

      {/* Challenges */}
      <ContentSection
        id="challenges"
        title="The Challenge"
        description="APIs and integrations create security risks without proper controls."
      >
        <div className="space-y-4">
          {challenges.map((challenge, index) => (
            <div key={index} className="flex items-start gap-4 p-4 rounded-lg bg-red-500/5 border border-red-500/20">
              <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <span>{challenge}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Solution */}
      <ContentSection
        id="solution"
        title="The Matricks Solution"
        background="muted"
      >
        <FeatureShowcase features={solutions} columns={2} variant="cards" />
      </ContentSection>

      {/* Capabilities */}
      <ContentSection
        id="capabilities"
        title="Security Capabilities"
        description="Comprehensive security features for enterprise requirements."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {securityCapabilities.map((cap) => (
            <div key={cap.category} className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold">{cap.category}</h3>
              <ul className="mt-4 space-y-2">
                {cap.items.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-green-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Compliance */}
      <ContentSection
        id="compliance"
        title="Compliance Support"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-xl border bg-card p-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 mb-4">
              <FileCheck className="h-8 w-8 text-primary" />
            </div>
            <h3 className="font-semibold">SOC2 Type II</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Annual audits verify our security controls meet the highest standards.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 mb-4">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <h3 className="font-semibold">GDPR</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Data processing agreements, right to deletion, and data portability.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 mb-4">
              <Lock className="h-8 w-8 text-primary" />
            </div>
            <h3 className="font-semibold">HIPAA</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Business Associate Agreements available for healthcare data.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Benefits */}
      <ContentSection id="benefits" title="Key Benefits">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            "Consistent security across all integrations",
            "Meet compliance requirements faster",
            "Complete audit trail for forensics",
            "Reduced risk of data breaches",
            "Fine-grained control without complexity",
            "Security that doesn't slow developers",
          ].map((benefit) => (
            <div key={benefit} className="flex items-center gap-3">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
              <span>{benefit}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Secure Your Integrations"
        description="Implement enterprise-grade security without slowing down your team."
        primaryAction={{ label: "Contact Sales", href: "/contact" }}
        secondaryAction={{ label: "View Security Features", href: "/features/security" }}
        background="primary"
      />
    </div>
  );
}
